import React, { useEffect, useMemo, useState } from 'react'
import { Autocomplete, Fab, Grid, TextField } from '@mui/material'
import { Add } from '@mui/icons-material'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import { AIRPORT_COLLECTION } from '../../_constants/globals'
import { MARKER_SVG_PATH, MURET_LAT_LNG } from '../../_constants/misc'
import { useFirestore } from '../../hooks/useFirestore'
import AirportCard from './AirportCard'
import WriteAirportForm from './WriteAirportForm'


export const AirportOverviewPanel = () => {
  
  const airportHooks = useFirestore(AIRPORT_COLLECTION)
  const airports = airportHooks.getDocs()
  
  const [map, setMap] = useState(null)
  const [editMode, setEditMode] = useState(false)
  const [selectedAirportId, selectAirportId] = useState(null)
  const [latLng, setLatLng] = useState(null)
  
  const { isLoaded: mapLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  })
  
  const selectedAirport = useMemo(() => selectedAirportId === 'new' && editMode
    ? { latitude: 46.180991053228716, longitude: 2.639263955210033 }
    : airports?.find(a => a.id === selectedAirportId), [airports, selectedAirportId, editMode])
  
  useEffect(() => {
    if (selectedAirport && selectedAirport.ICAO !== 'LFBR') {
      const bounds = new window.google.maps.LatLngBounds()
      bounds.extend(MURET_LAT_LNG)
      bounds.extend({ lat: selectedAirport.latitude, lng: selectedAirport.longitude })
      map.fitBounds(bounds)
      setLatLng({ lat: selectedAirport.latitude, lng: selectedAirport.longitude })
    }
    else if (map && window.google) {
      const bounds = new window.google.maps.LatLngBounds()
      bounds.extend({ lat: 50.55235757283226, lng: -5.838781619631178 })
      bounds.extend({ lat: 42.13718683676462, lng: 8.243741025317748 })
      map.fitBounds(bounds)
    }
  }, [selectedAirport, map, window.google])
  
  return <Grid container spacing={1} sx={{ minHeight: '426px', m: '0 auto', maxWidth: '936px' }}>
    <Grid item size={{ xs: 12, md: 6 }} sx={{ zIndex: 1, ml: { xs: 1, md: 0 } }}>
      {airports && <Autocomplete
        options={airports}
        getOptionLabel={option => option.name || ''}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        renderInput={params => <TextField variant='standard' {...params} label='Search' />}
        value={selectedAirport || null}
        onChange={(_, newValue) => selectAirportId(newValue?.id)}
      />}
      {selectedAirport && !editMode && <AirportCard airport={selectedAirport} setEditMode={setEditMode} />}
      {editMode && <WriteAirportForm airport={selectedAirport} latLng={latLng} onComplete={() => setEditMode(false)} />}
    </Grid>
    <Grid item size={{ xs: 12, md: 6 }} sx={{ zIndex: 1, ml: { xs: 1, md: 0 } }}>
      {mapLoaded && <GoogleMap
        mapContainerStyle={{ width: '100%', aspectRatio: 1 }}
        options={{
          streetViewControl: false,
          zoomControl: true,
          mapTypeControl: true,
        }}
        onLoad={map => {
          setMap(map)
          const bounds = new window.google.maps.LatLngBounds()
          bounds.extend({ lat: 50.55235757283226, lng: -5.838781619631178 })
          bounds.extend({ lat: 42.13718683676462, lng: 8.243741025317748 })
          map.fitBounds(bounds)
        }}
      >
        {selectedAirport
          ? <>
            <Marker
              position={MURET_LAT_LNG}
              icon={{
                path: 'M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z',
                anchor: { x: 15, y: 15 },
                fillOpacity: 1,
              }}
              onClick={() => selectAirportId(null)}
            />
            {selectedAirport.ICAO !== 'LFBR' && <Marker
              position={latLng || { lat: selectedAirport.latitude, lng: selectedAirport.longitude }}
              icon={{ path: MARKER_SVG_PATH, fillOpacity: 1, anchor: { x: 15, y: 15 } }}
              onClick={() => selectAirportId(null)}
              onDrag={e => setLatLng({ lat: e.latLng.lat(), lng: e.latLng.lng() })}
              draggable={editMode}
            />}
          </>
          : airports?.map(airport => <Marker
            key={airport.id}
            position={{ lat: airport.latitude, lng: airport.longitude }}
            icon={{ path: MARKER_SVG_PATH, fillOpacity: 1, anchor: { x: 15, y: 15 } }}
            onClick={() => selectAirportId(airport.id)}
          />)
        }
      </GoogleMap>}
    </Grid>
    <Fab
      color='secondary'
      aria-label='add'
      onClick={() => {
        selectAirportId('new')
        setEditMode(true)
      }}
      sx={{
        position: 'fixed',
        right: theme => theme.spacing(3),
        bottom: theme => theme.spacing(3),
      }}
    >
      <Add />
    </Fab>
  </Grid>
}

export default AirportOverviewPanel
